import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { resolveState, STATES_LIST } from '../data/brazil-state-centroids';
import { PROGRAMS } from '../components/agenda/agenda-shared';
import { FiltersBar } from '../components/agenda/FiltersBar';
import {
  TurmaCard,
  EmptyState,
  ProgramShortcuts,
  FinalCTA,
} from '../components/agenda/AgendaParts';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AgendaPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [events, setEvents] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [loading, setLoading] = useState(true);

  const [program, setProgram] = useState(searchParams.get('program') || '');
  const [modality, setModality] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [city, setCity] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const [evRes, frRes] = await Promise.all([
          axios.get(`${API_URL}/public/events`),
          axios.get(`${API_URL}/public/franchises`),
        ]);
        setEvents(Array.isArray(evRes.data) ? evRes.data : []);
        setFranchises(Array.isArray(frRes.data) ? frRes.data : []);
      } catch (err) {
        console.warn('Failed to load agenda data', err?.message);
      } finally {
        setLoading(false);
      }
    })();
    window.scrollTo(0, 0);
  }, []);

  const franchiseById = useMemo(
    () => Object.fromEntries(franchises.map((f) => [f.franchise_id, f])),
    [franchises],
  );

  const availableStates = useMemo(() => {
    const codes = new Set();
    franchises.forEach((f) => {
      const st = resolveState(f.state);
      if (st) codes.add(st.code);
    });
    return STATES_LIST.filter((s) => codes.has(s.code));
  }, [franchises]);

  const availableCities = useMemo(() => {
    if (!stateCode) return [];
    const set = new Set();
    events.forEach((e) => {
      const fr = e.franchise_id ? franchiseById[e.franchise_id] : null;
      const evStateCode =
        resolveState(e.state)?.code || (fr ? resolveState(fr.state)?.code : null);
      if (evStateCode !== stateCode) return;
      if (e.city) set.add(e.city);
      else if (fr?.city) set.add(fr.city);
    });
    franchises.forEach((f) => {
      const fs = resolveState(f.state);
      if (fs?.code !== stateCode) return;
      if (f.city) set.add(f.city.trim());
      (f.cities_served || []).forEach((c) => c && set.add(String(c).trim()));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [events, franchises, franchiseById, stateCode]);

  const filteredEvents = useMemo(() => {
    return events.filter((e) => {
      if (program && e.product_id !== program) return false;
      if (modality) {
        const m = (e.modality || e.event_type || '').toLowerCase();
        if (m !== modality) return false;
      }
      if (stateCode) {
        const fr = e.franchise_id ? franchiseById[e.franchise_id] : null;
        const evStateCode =
          resolveState(e.state)?.code || (fr ? resolveState(fr.state)?.code : null);
        if (evStateCode !== stateCode) return false;
      }
      if (city) {
        const fr = e.franchise_id ? franchiseById[e.franchise_id] : null;
        const evCity = e.city || fr?.city || '';
        if (evCity.toLowerCase() !== city.toLowerCase()) return false;
      }
      if (dateFrom) {
        const fromTs = new Date(dateFrom).getTime();
        const eventTs = new Date(e.event_date).getTime();
        if (eventTs < fromTs) return false;
      }
      if (dateTo) {
        const toTs = new Date(dateTo).getTime() + 24 * 60 * 60 * 1000;
        const eventTs = new Date(e.event_date).getTime();
        if (eventTs > toTs) return false;
      }
      return true;
    });
  }, [events, program, modality, stateCode, city, dateFrom, dateTo, franchiseById]);

  const turmasByProgram = useMemo(() => {
    const c = {};
    PROGRAMS.forEach((p) => (c[p.slug] = 0));
    events.forEach((e) => {
      if (e.product_id && c[e.product_id] !== undefined) c[e.product_id] += 1;
    });
    return c;
  }, [events]);

  const clearFilters = useCallback(() => {
    setProgram('');
    setModality('');
    setStateCode('');
    setCity('');
    setDateFrom('');
    setDateTo('');
    setSearchParams({});
  }, [setSearchParams]);

  const scrollToResults = () =>
    document.querySelector('#agenda-results')?.scrollIntoView({ behavior: 'smooth' });

  const applyProgramFilter = (slug) => {
    setProgram(slug);
    scrollToResults();
  };

  const hasFilters = program || modality || stateCode || city || dateFrom || dateTo;

  return (
    <>
      <SEO
        title="Agenda IE — Próximas Turmas"
        description="Encontre a próxima turma do Instituto Eneagrama por programa, modalidade, estado, cidade e data."
        path="/agenda"
      />
      <Header />
      <main className="min-h-screen">
        {/* HERO */}
        <section
          className="relative pt-32 pb-16 border-b border-neutral-900"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
          data-testid="agenda-hero"
        >
          <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-12 text-center">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block">
              Próximas turmas
            </span>
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-[#EAEAEA] mb-4">
              Agenda IE
            </h1>
            <p className="text-lg text-neutral-400 max-w-3xl mx-auto">
              Encontre a próxima turma por programa, localização ou data.
            </p>
          </div>
        </section>

        <FiltersBar
          program={program} setProgram={setProgram}
          modality={modality} setModality={setModality}
          stateCode={stateCode} setStateCode={setStateCode}
          city={city} setCity={setCity}
          dateFrom={dateFrom} setDateFrom={setDateFrom}
          dateTo={dateTo} setDateTo={setDateTo}
          availableStates={availableStates}
          availableCities={availableCities}
          onApply={scrollToResults}
          onClear={clearFilters}
        />

        {/* RESULTS */}
        <section
          id="agenda-results"
          className="relative py-16"
          style={{ background: 'rgba(10, 10, 10, 0.78)' }}
        >
          <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12">
            <div className="flex items-baseline justify-between mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-[#EAEAEA]">
                {filteredEvents.length > 0
                  ? `${filteredEvents.length} turma${filteredEvents.length > 1 ? 's' : ''} encontrada${
                      filteredEvents.length > 1 ? 's' : ''
                    }`
                  : 'Nenhuma turma encontrada'}
              </h2>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs font-mono uppercase tracking-widest text-accent-orange hover:text-[#E55D00]"
                  data-testid="results-clear"
                >
                  Limpar filtros →
                </button>
              )}
            </div>

            {loading ? (
              <div className="text-center py-12 text-neutral-500">Carregando turmas…</div>
            ) : filteredEvents.length > 0 ? (
              <div
                className="grid grid-cols-1 md:grid-cols-2 gap-6"
                data-testid="agenda-cards-grid"
              >
                {filteredEvents.map((e) => (
                  <TurmaCard
                    key={e.event_id}
                    event={e}
                    franchise={e.franchise_id ? franchiseById[e.franchise_id] : null}
                  />
                ))}
              </div>
            ) : (
              <EmptyState />
            )}
          </div>
        </section>

        <ProgramShortcuts turmasByProgram={turmasByProgram} onPick={applyProgramFilter} />
        <FinalCTA />
      </main>
      <Footer />
    </>
  );
};

export default AgendaPage;
